import {
  Box,
  Button,
  Heading,
  VStack,
  Text,
  Flex,
  Link,
  useToast,
  Stack,
  FormControl,
  Input,
  FormErrorMessage,
  InputGroup,
  Divider,
  HStack,
  CloseButton,
  Image,
} from "@chakra-ui/react";

import { useAuth } from "../../../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faUser } from "@fortawesome/free-solid-svg-icons";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";
import { updateBookingContact, deleteBookingImages } from "../../../services/bookings";
import { ROLES } from "../../../constants";
import {
  contactFromBooking,
  contactFromUser,
  getVisitorCheckoutContact,
  saveVisitorCheckoutContact,
} from "../../../utils/visitorCheckoutContact";
import { useDropZoneUpload } from "../../../utils/customHooks";
import { SingleDatepicker } from "chakra-dayzed-datepicker";
import { useState, useMemo } from "react";
import { submitPhotosToBooking } from "../../../services/bookings";
import { usePhotoUpload } from "../../../utils/sharedMutations";
import { optionalTelephoneYup } from "../../../utils/phoneValidation";
import {
  CLIENT_EMAIL_REGEX,
  bookingHasRequiredDocuments,
  preparePhotosForUpload,
  mapPersistedBookingDocument,
  truncateName,
  minimumPassportExpiryDateCET,
  isCalendarDayBefore,
} from "../../../utils/helpers";
import { createForeignPartnerBooking } from "../../../services/affiliate";
import { useMatch } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { phoneNumberInputStyleInherit } from "../../../styles/phoneNumberInputStyleInherit";
import "./UserDataStep.scss";
import { upperCase } from "lodash";
import pdfPreview from "../../../assets/pdf_preview_icon.png";

const toFormValue = (value) => (value == null ? "" : String(value));

const toInitialPassportExpiryDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

function UserDataStep({ nextStep, endDate, bookingId, booking, isForeignPartnerReservation, foreignPartnerBookingClientData }) {
  const { currentUser, isAuthenticated, isLoadingUser } =
    useAuth();

  const { t } = useTranslation("common");
  const toast = useToast();
  const [isPhoneInputFocused, setIsPhoneInputFocused] = useState(false);

  const match = useMatch("/book/:bookingId");
  const bookingUuid = match?.params.bookingId ?? booking?.uuid;
  const queryClient = useQueryClient();

  const formatDateForApi = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  };

  const resolveInitialContact = () => {
    if (
      isForeignPartnerReservation &&
      foreignPartnerBookingClientData &&
      !foreignPartnerBookingClientData?.error
    ) {
      return {
        firstName: toFormValue(foreignPartnerBookingClientData?.clientFirstName),
        lastName: toFormValue(foreignPartnerBookingClientData?.clientLastName),
        email: toFormValue(foreignPartnerBookingClientData?.email),
        telephoneNumber: toFormValue(foreignPartnerBookingClientData?.telephoneNumber),
        nameOnPassport: toFormValue(foreignPartnerBookingClientData?.nameOnPassport),
        passportExpiresAt: foreignPartnerBookingClientData?.passportExpirationDate ?? null,
      };
    }

    const fromBooking = contactFromBooking(booking);
    if (fromBooking && (fromBooking.email || fromBooking.firstName)) {
      return fromBooking;
    }

    if (currentUser?.type === ROLES.GUEST) {
      return contactFromUser(currentUser, { includePassport: true });
    }

    if (currentUser?.type === ROLES.VISITOR) {
      return (
        getVisitorCheckoutContact() ||
        contactFromUser(currentUser) || {
          firstName: "",
          lastName: "",
          email: "",
          telephoneNumber: "",
          nameOnPassport: "",
          passportExpiresAt: null,
        }
      );
    }

    if (
      currentUser &&
      foreignPartnerBookingClientData?.error &&
      currentUser.type !== ROLES.FOREIGN_PARTNER
    ) {
      return contactFromUser(currentUser);
    }

    return {
      firstName: "",
      lastName: "",
      email: "",
      telephoneNumber: "",
      nameOnPassport: "",
      passportExpiresAt: null,
    };
  };

  const resolvedContact = resolveInitialContact();

  const checkoutOrDropOffDate =
    endDate ?? (booking?.checkOutDate ? new Date(booking.checkOutDate) : undefined);

  const passportExpiryMinDate = minimumPassportExpiryDateCET(checkoutOrDropOffDate);

  const passportExpiryFieldDisabled =
    isForeignPartnerReservation && currentUser?.type === ROLES.FOREIGN_PARTNER;

  const validationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string().required(t("requiredFieldMessage")),
        lastName: Yup.string().required(t("requiredFieldMessage")),
        email: Yup.string()
          .required(t("requiredFieldMessage"))
          .matches(CLIENT_EMAIL_REGEX, t("invalidEmailMessage")),
        telephoneNumber: optionalTelephoneYup(t),
        nameOnPassport: Yup.string().required(t("requiredFieldMessage")),
        passportExpiresAt: passportExpiryFieldDisabled
          ? Yup.date().nullable()
          : Yup.date()
              .nullable()
              .required(t("bookingPassportExpiryRequired"))
              .test(
                "min-departure-plus-three-months",
                t("bookingPassportExpiryTooEarly"),
                (value) => {
                  if (!value || !passportExpiryMinDate) return true;
                  return !isCalendarDayBefore(value, passportExpiryMinDate);
                }
              ),
      }),
    [t, passportExpiryFieldDisabled, passportExpiryMinDate]
  );

  const updateBookingContactMutation = useMutation({
    mutationFn: (userData) =>
      updateBookingContact(bookingUuid, {
        ...userData,
        passportExpiresAt: formatDateForApi(userData.passportExpiresAt),
      }),
    onSuccess: (_data, variables) => {
      if (currentUser?.type === ROLES.VISITOR) {
        saveVisitorCheckoutContact({
          ...variables,
          passportExpiresAt: formatDateForApi(variables.passportExpiresAt),
        });
      }
      toast({
        title: t("bookingDetailsSaved"),
        status: "success",
      });
    },
    onError: () => {
      toast({
        title: t("affiliateCreateError"),
        status: "error",
      });
    },
  });

  const createForeignPartnerBookingMutation = useMutation({
    mutationFn: (foreignPartnerBooking) => {
      if (currentUser?.type === ROLES.FOREIGN_PARTNER )
        return createForeignPartnerBooking(bookingId,
            {...foreignPartnerBooking,
              clientFirstName: foreignPartnerBooking.firstName,
              clientLastName: foreignPartnerBooking.lastName,
              passportExpiresAt: formatDateForApi(foreignPartnerBooking.passportExpiresAt),
            },
        )
        .then((res) => {
              //setCurrentUser(res.data);
            }
        )
    },
    onError: (error) => {
      toast({
        title: t("affiliateCreateError"),
        status: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: t("affiliateReservationSuccess"),
        status: "success",
      });
    },
  });

  const emptyFormValues = {
    firstName: "",
    lastName: "",
    email: "",
    telephoneNumber: "",
    nameOnPassport: "",
    passportExpiresAt: null,
  };

  const initialFormValues =
    isLoadingUser ||
    (currentUser?.type === ROLES.FOREIGN_PARTNER && !isForeignPartnerReservation)
      ? emptyFormValues
      : {
          firstName: toFormValue(resolvedContact.firstName),
          lastName: toFormValue(resolvedContact.lastName),
          email: toFormValue(resolvedContact.email),
          telephoneNumber: toFormValue(resolvedContact.telephoneNumber),
          nameOnPassport: toFormValue(resolvedContact.nameOnPassport),
          passportExpiresAt: toInitialPassportExpiryDate(
            resolvedContact.passportExpiresAt
          ),
        };

  const passportImages = (booking?.images ?? []).filter(
    (image) => image.reservationPassport
  );
  const ticketImages = (booking?.images ?? []).filter(
    (image) => !image.reservationPassport
  );

  const initialPassportPhotos = passportImages.map(mapPersistedBookingDocument);

  const initialTicketPhotos = ticketImages.map(mapPersistedBookingDocument);

  const { selectedFiles, getRootProps, getInputProps, handleRemoveFile } =
    useDropZoneUpload(initialPassportPhotos, () =>
      setShowPassportPhotoErrorMessage(false)
    );

  const {
    selectedFiles: selectedFiles2,
    getRootProps: getRootProps2,
    getInputProps: getInputProps2,
    handleRemoveFile: handleRemoveFile2,
  } = useDropZoneUpload(initialTicketPhotos, () =>
    setShowPhotosErrorMessage(false)
  );

  // Removes a photo from the UI and, when it was already persisted (has an id),
  // deletes it server-side so the booking state stays honest across reloads.
  const removePhoto = (index, image, localRemove) => {
    localRemove(index, image.id);
    if (!image.id) return;

    deleteBookingImages(bookingId, [image.id])
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["booking", bookingUuid] });
        queryClient.invalidateQueries({
          queryKey: ["booking-unique", bookingUuid],
        });
      })
      .catch(() => {
        toast({ title: t("affiliateCreateError"), status: "error" });
      });
  };

  const handleRemovePassportPhoto = (index, image) =>
    removePhoto(index, image, handleRemoveFile);

  const handleRemoveTicketPhoto = (index, image) =>
    removePhoto(index, image, handleRemoveFile2);

  const submitPhotosMutation = usePhotoUpload(
    ({ bookingId, photos, reservationPassport }) =>
      submitPhotosToBooking(bookingId, photos, reservationPassport)
  );

  const [showPassportPhotoErrorMessage, setShowPassportPhotoErrorMessage] =
    useState(false);
  const [showPhotosErrorMessage, setShowPhotosErrorMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (values, photos1, photos2) => {
    setIsSubmitting(true);
    try {
      if (photos1.length > 0) {
        await submitPhotosMutation.mutateAsync({
          bookingId: bookingId,
          photos: preparePhotosForUpload(photos1),
          reservationPassport: true,
        });
      }

      if (photos2.length > 0) {
        await submitPhotosMutation.mutateAsync({
          bookingId: bookingId,
          photos: preparePhotosForUpload(photos2),
          reservationPassport: false,
        });
      }

      if (currentUser.type === ROLES.FOREIGN_PARTNER) {
        if (!isForeignPartnerReservation) {
          await createForeignPartnerBookingMutation.mutateAsync(values);
        }
      } else {
        await updateBookingContactMutation.mutateAsync(values);
      }

      await queryClient.invalidateQueries({ queryKey: ["booking", bookingUuid] });
      queryClient.invalidateQueries({
        queryKey: ["booking-unique", bookingUuid],
      });

      if (currentUser.type === ROLES.FOREIGN_PARTNER) {
        await queryClient.invalidateQueries({
          queryKey: ["get-foreign-partner-booking", bookingUuid],
        });
        await queryClient.refetchQueries({ queryKey: ["booking", bookingUuid] });
        await queryClient.refetchQueries({
          queryKey: ["get-foreign-partner-booking", bookingUuid],
        });

        const refreshedBooking = queryClient.getQueryData(["booking", bookingUuid])?.data;
        if (!bookingHasRequiredDocuments(refreshedBooking)) {
          toast({
            title: t("bookingPhotosRequired"),
            status: "error",
          });
          return;
        }
      }

      if (currentUser.type !== ROLES.FOREIGN_PARTNER) {
        nextStep();
      }
    } catch {
      // Individual mutations already surface errors via toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack width="full" gap="5" alignItems="flex-start">
      {((!isLoadingUser && !isAuthenticated) ||
        currentUser?.type === ROLES.VISITOR) && (
        <Box
          width="full"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          padding="15px"
        >
          <Flex gap="5" alignItems="center">
            <FontAwesomeIcon icon={faUser} />
            <Text fontSize="sm">
              {t("bookingNotSignedIn")}{" "}
              <Link
                color="blue.500"
                href={`/login?returnUrl=${encodeURIComponent(
                  `/book/${bookingUuid}`
                )}`}
              >
                {t("bookingSignIn")}
              </Link>{" "}
              {t("bookingSignInToBook")}{" "}
              <Link
                color="blue.500"
                href={`/register?returnUrl=${encodeURIComponent(
                  `/book/${bookingUuid}`
                )}`}
              >
                {t("bookingRegister")}
              </Link>{" "}
              {t("bookingRegisterToManage")}
            </Text>
          </Flex>
        </Box>
      )}

      <Box
        width="full"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        padding="15px"
      >
        <VStack align="flex-start" spacing="2">
          <Heading as="h3" size="sm" mb="15px">
            {currentUser?.type === ROLES.FOREIGN_PARTNER
              ? t("affiliateClientDetails")
              : t("bookingYourDetails")}
          </Heading>
          <Formik
            key={
              isLoadingUser
                ? "user-data-loading"
                : `user-data-${toFormValue(resolvedContact.email)}-${formatDateForApi(
                    toInitialPassportExpiryDate(resolvedContact.passportExpiresAt)
                  )}`
            }
            initialValues={initialFormValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            validateOnMount={true}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values) => {
              const newlyAddedFiles = selectedFiles
                .filter((file) => !file.id)
                .map((file) => file.file);

              const newlyAddedFiles2 = selectedFiles2
                .filter((file) => !file.id)
                .map((file) => file.file);

              const activePassportCount = selectedFiles.filter(
                (file) => !file._destroy
              ).length;
              const activeTicketCount = selectedFiles2.filter(
                (file) => !file._destroy
              ).length;

              if (activePassportCount < 1) {
                setShowPassportPhotoErrorMessage(true);
                return;
              }

              if (activeTicketCount < 1) {
                setShowPhotosErrorMessage(true);
                return;
              }

              onSubmit(values, newlyAddedFiles, newlyAddedFiles2);
            }}
          >
            {(props) => {
              const fieldError = (name) => Boolean(props.errors[name]);

              return (
              <Form>
                <Stack spacing={4}>
                  <Flex gap="3">
                    <Field name="firstName">
                      {({ field, form }) => (
                        <FormControl isInvalid={fieldError("firstName")}>
                          <Text mb="6px" fontSize={"sm"}>
                            {t("firstNameInputPlaceholder")}
                          </Text>
                          <Input {...field} variant="outline"
                                // disabled={(currentUser?.type !== ROLES.VISITOR && currentUser?.type !== ROLES.FOREIGN_PARTNER) || (foreignPartnerBookingClientData && currentUser.type === ROLES.FOREIGN_PARTNER)}
                              disabled={(isForeignPartnerReservation && currentUser?.type === ROLES.FOREIGN_PARTNER)}
                          />
                          <FormErrorMessage>
                            {form.errors.firstName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="lastName">
                      {({ field, form }) => (
                        <FormControl isInvalid={fieldError("lastName")}>
                          <Text mb="6px" fontSize={"sm"}>
                            {t("lastNameInputPlaceholder")}
                          </Text>
                          <Input {...field} variant="outline"
                                 //disabled={(currentUser?.type !== ROLES.VISITOR && currentUser?.type !== ROLES.FOREIGN_PARTNER) || (foreignPartnerBookingClientData && currentUser.type === ROLES.FOREIGN_PARTNER)}
                                 disabled={isForeignPartnerReservation && currentUser?.type === ROLES.FOREIGN_PARTNER}

                          />
                          <FormErrorMessage>
                            {form.errors.lastName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                  <Field name="email">
                    {({ field, form }) => (
                      <FormControl isInvalid={fieldError("email")}>
                        <Text mb="6px" fontSize={"sm"}>
                          {t("emailInputPlaceholder")}
                        </Text>
                        <Input {...field} variant="outline"
                             //  disabled={(currentUser?.type !== ROLES.VISITOR && currentUser?.type !== ROLES.FOREIGN_PARTNER) || (foreignPartnerBookingClientData && currentUser.type === ROLES.FOREIGN_PARTNER)}
                               disabled={isForeignPartnerReservation && currentUser?.type === ROLES.FOREIGN_PARTNER}

                        />
                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="telephoneNumber">
                    {({ field, form }) => (
                      <FormControl isInvalid={fieldError("telephoneNumber")}>
                        <Text mb="6px" fontSize={"sm"}>
                          {t("phoneNumberInputPlaceholder")}{" "}
                          <Text as="span" color="gray.500">
                            ({t("phoneNumberOptionalHint")})
                          </Text>
                        </Text>
                        <InputGroup
                        >
                          <PhoneInput
                            as="input"
                           // disabled={(currentUser?.type !== ROLES.VISITOR && currentUser?.type !== ROLES.FOREIGN_PARTNER) || (foreignPartnerBookingClientData && currentUser.type === ROLES.FOREIGN_PARTNER)}
                            disabled={isForeignPartnerReservation && currentUser?.type === ROLES.FOREIGN_PARTNER}
                            style={{
                              ...phoneNumberInputStyleInherit,
                              ...(isPhoneInputFocused
                                ? {
                                    borderColor: "#3182ce",
                                    boxShadow: "0 0 0 1px #3182ce",
                                  }
                                : {}),
                              ...(form.errors.telephoneNumber
                                ? {
                                    borderColor: "#E53E3E",
                                    boxShadow: "0 0 0 1px #E53E3E",
                                  }
                                : {}),
                            }}
                            onFocus={() => setIsPhoneInputFocused(true)}
                            onBlur={() => setIsPhoneInputFocused(false)}
                            defaultCountry={upperCase(
                              localStorage.getItem("currencyCode") === "eu"
                                ? ""
                                : localStorage.getItem("currencyCode")
                            )}
                            placeholder=""
                            value={field.value ?? ""}
                            onChange={(value) => {
                              form.setFieldValue("telephoneNumber", value);
                            }}
                          />
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.telephoneNumber}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Divider />
                  <Flex width="full" direction="column" gap={5}>
                    <Heading as="h3" size="sm">
                      {t("bookingDocuments")}
                    </Heading>
                    <div>
                      <Text mb="6px" fontSize={"sm"}>
                        {t("bookingUploadPassport")}
                      </Text>
                      <div
                        {...getRootProps()}
                        style={{ padding: "20px", border: "2px dashed #ccc" }}
                      >
                        <input {...getInputProps()} />
                        <p>{t("dropZoneMessage")}</p>
                      </div>

                      <Box mt={selectedFiles.length > 0 ? 2 : 0}>
                        <HStack wrap="wrap" spacing="4">
                          {selectedFiles.map((image, index) => {
                            if (image._destroy) {
                              return null;
                            }
                            return (
                              <Box key={index}>
                                <Box
                                  width="50px"
                                  height="50px"
                                  position={"relative"}
                                >
                                  <VStack>
                                    <Image
                                      maxWidth="50px"
                                      maxHeight="50px"
                                      src={
                                        image.fileType === "pdf"
                                          ? pdfPreview
                                          : image.previewUrl || image.url
                                      }
                                    />
                                    <Text isTruncated fontSize="10px">
                                      {truncateName(image.name)}
                                    </Text>
                                  </VStack>
                                  <CloseButton
                                    size={"sm"}
                                    position={"absolute"}
                                    top={"-10px"}
                                    right={"-10px"}
                                    onClick={() =>
                                      handleRemovePassportPhoto(index, image)
                                    }
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </HStack>
                      </Box>
                      <Text
                        display={
                          showPassportPhotoErrorMessage ? "block" : "none"
                        }
                        mt={1}
                        fontSize="sm"
                        color="red.500"
                      >
                        {t("bookingPassportPhotoRequired")}
                      </Text>
                      <br />
                      <Text mb="6px" fontSize={"sm"}>
                        {t("bookingUploadPlaneTicket")}
                      </Text>
                      <div
                        id="guest_documents_dropzone"
                        {...getRootProps2()}
                        style={{
                          padding: "20px",
                          border: "2px dashed #ccc",
                        }}
                      >
                        <input {...getInputProps2()} />
                        <p>{t("dropZoneMessage")}</p>
                      </div>
                      <Box mt={selectedFiles2.length > 0 ? 2 : 0}>
                        <HStack wrap="wrap" spacing="4">
                          {selectedFiles2.map((image, index) => {
                            if (image._destroy) {
                              return null;
                            }
                            return (
                              <Box key={index}>
                                <Box
                                  width="50px"
                                  height="50px"
                                  position={"relative"}
                                >
                                  <VStack>
                                    <Image
                                      maxWidth="50px"
                                      maxHeight="50px"
                                      src={
                                        image.fileType === "pdf"
                                          ? pdfPreview
                                          : image.previewUrl || image.url
                                      }
                                    />
                                    <Text isTruncated fontSize="10px">
                                      {truncateName(image.name)}
                                    </Text>
                                  </VStack>
                                  <CloseButton
                                    size={"sm"}
                                    position={"absolute"}
                                    top={"-10px"}
                                    right={"-10px"}
                                    onClick={() =>
                                      handleRemoveTicketPhoto(index, image)
                                    }
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </HStack>
                      </Box>
                      <Text
                        display={showPhotosErrorMessage ? "block" : "none"}
                        mt={1}
                        fontSize="sm"
                        color="red.500"
                      >
                        {t("bookingPhotosRequired")}
                      </Text>
                    </div>

                    <Field name="passportExpiresAt">
                      {({ field, form }) => (
                        <FormControl isInvalid={fieldError("passportExpiresAt")}>
                          <Text mb="6px" fontSize={"sm"}>
                            {t("bookingPassportExpiryDate")}{" "}
                            <Text as="span" color="gray.500" fontWeight="normal">
                              ({t("bookingPassportExpiryDateHint")})
                            </Text>
                          </Text>

                          <SingleDatepicker
                            disabled={passportExpiryFieldDisabled}
                            date={field.value ?? undefined}
                            onDateChange={(date) => {
                              form.setFieldValue(
                                "passportExpiresAt",
                                date ?? null
                              );
                            }}
                          />
                          <FormErrorMessage>
                            {form.errors.passportExpiresAt}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name="nameOnPassport">
                      {({ field, form }) => (
                        <FormControl isInvalid={fieldError("nameOnPassport")}>
                          <Text mb="6px" fontSize={"sm"}>
                            {t("bookingNameOnPassport")}
                          </Text>
                          <Input {...field} variant="outline"
                                 disabled={isForeignPartnerReservation && currentUser?.type === ROLES.FOREIGN_PARTNER}
                              //  disabled={currentUser?.type !== ROLES.VISITOR && currentUser?.type !== ROLES.FOREIGN_PARTNER }
                          />
                          <FormErrorMessage>
                            {form.errors.nameOnPassport}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>

                  <Button
                    colorScheme="teal"
                    type="submit"
                    variant="solid"
                    isLoading={
                      isSubmitting ||
                      updateBookingContactMutation.isLoading ||
                      createForeignPartnerBookingMutation.isLoading ||
                      submitPhotosMutation.isLoading
                    }
                    isDisabled={isSubmitting || !props.isValid}
                    rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
                  >
                    {t("bookingFinalStep")}
                  </Button>
                  {
                    isForeignPartnerReservation &&
                      <VStack w={"200px"}>
                        <HStack w={"100%"} justify="space-between">
                          <Text flex="1" fontWeight="normal">{t("bookingAffiliatePrice")}</Text>
                          <Text fontWeight="bold">{`EUR ${foreignPartnerBookingClientData.affiliatePrice}`}</Text>
                        </HStack>
                        <HStack w={"100%"} justify="space-between">
                          <Text flex="1" fontWeight="normal">{t("bookingCommission")}</Text>
                          <Text fontWeight="bold">{`EUR ${foreignPartnerBookingClientData.commission}`}</Text>
                        </HStack>
                      </VStack>
                  }
                </Stack>
              </Form>
            );
            }}
          </Formik>
        </VStack>
      </Box>
    </VStack>
  );
}

export default UserDataStep;
